import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { matchMap, matches } from "@/mocks/matches";
import { teamMap } from "@/mocks/teams";
import { useFavorites } from "@/hooks/useFavorites";
import Breadcrumb from "@/components/feature/Breadcrumb";
import TeamLogo from "@/components/base/TeamLogo";
import StatusBadge from "@/components/base/StatusBadge";
import VideoPlayerDemo from "@/pages/match/components/VideoPlayerDemo";
import { formatTime, formatFullDate } from "@/utils/format";

const PLAYER_BG =
  "https://readdy.ai/api/search-image?query=Stylized%20dark%20basketball%20arena%20court%20with%20dramatic%20spotlights%20and%20blue%20orange%20lighting%2C%20empty%20court%20view%20from%20stands%2C%20cinematic%20sports%20background%2C%20no%20text&width=1600&height=900&seq=nba-match-cover&orientation=landscape";

export default function MatchDetail() {
  const { id } = useParams();
  const match = id ? matchMap[id] : undefined;
  const { isFavorite, toggleFavorite } = useFavorites();
  const [copied, setCopied] = useState(false);

  const related = useMemo(() => {
    if (!match) return [];
    return matches
      .filter((m) => m.id !== match.id && (m.homeTeamId === match.homeTeamId || m.awayTeamId === match.homeTeamId || m.homeTeamId === match.awayTeamId || m.awayTeamId === match.awayTeamId))
      .slice(0, 3);
  }, [match]);

  if (!match) {
    return (
      <div className="px-4 md:px-8 py-16">
        <div className="mx-auto flex max-w-lg flex-col items-center rounded-lg border border-dashed border-background-300 bg-background-100 px-6 py-14 text-center">
          <i className="ri-error-warning-line text-4xl text-accent-500" />
          <h1 className="mt-3 font-heading text-xl font-bold text-foreground-950">赛事不存在</h1>
          <p className="mt-2 text-sm text-foreground-500">该赛事可能已结束或被移除，请返回直播赛事页浏览其他比赛。</p>
          <Link
            to="/live"
            className="mt-5 inline-flex items-center gap-1 rounded-md bg-primary-500 px-4 py-2 text-sm font-semibold text-background-50 whitespace-nowrap transition-colors hover:bg-primary-600"
          >
            <i className="ri-arrow-left-line" /> 返回直播赛事
          </Link>
        </div>
      </div>
    );
  }

  const home = teamMap[match.homeTeamId];
  const away = teamMap[match.awayTeamId];
  const favorited = isFavorite(match.id);
  const hasScore = match.status !== "upcoming";

  const handleShare = async () => {
    try {
      const url = `${window.location.origin}${__BASE_PATH__}match/${match.id}`;
      await navigator.clipboard.writeText(url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  return (
    <div className="px-4 md:px-8 py-6 md:py-8">
      <Breadcrumb items={[{ label: "直播赛事", to: "/live" }, { label: `${home?.name} vs ${away?.name}` }]} />

      <header className="mt-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
            {home?.city}{home?.name} <span className="text-accent-500">VS</span> {away?.city}{away?.name}
          </h1>
          <p className="mt-1.5 text-sm text-foreground-500">
            {formatFullDate(match.startTime)} {formatTime(match.startTime)} · {match.stage} · {match.venue}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => toggleFavorite(match.id)}
            className={`inline-flex items-center gap-1.5 rounded-md px-3.5 py-2 text-sm font-semibold whitespace-nowrap transition-colors ${
              favorited
                ? "bg-accent-500 text-background-50 hover:bg-accent-600"
                : "border border-background-300 bg-background-50 text-foreground-700 hover:bg-background-100"
            }`}
          >
            <i className={favorited ? "ri-bookmark-3-fill" : "ri-bookmark-3-line"} />
            {favorited ? "已收藏" : "收藏"}
          </button>
          <button
            type="button"
            onClick={handleShare}
            className="inline-flex items-center gap-1.5 rounded-md border border-background-300 bg-background-50 px-3.5 py-2 text-sm font-semibold text-foreground-700 whitespace-nowrap transition-colors hover:bg-background-100"
          >
            <i className={copied ? "ri-check-line text-secondary-600" : "ri-share-line"} />
            {copied ? "链接已复制" : "分享"}
          </button>
        </div>
      </header>

      <section className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <div className="flex items-center justify-between gap-3 rounded-lg bg-primary-900 px-4 py-5 md:px-8 md:py-6">
            <div className="flex flex-col items-center gap-2 flex-1">
              {home && <TeamLogo team={home} size="lg" />}
              <span className="text-sm font-semibold text-background-50">{home?.city}{home?.name}</span>
              <span className="text-[11px] text-background-400">{home?.record}</span>
            </div>
            <div className="flex flex-col items-center px-3">
              {hasScore ? (
                <span className="font-heading text-4xl font-bold text-background-50 md:text-5xl">
                  {match.homeScore} : {match.awayScore}
                </span>
              ) : (
                <span className="font-heading text-3xl font-bold text-background-50">VS</span>
              )}
              <span className="mt-2">
                <StatusBadge status={match.status} period={match.status === "live" ? match.period : undefined} />
              </span>
            </div>
            <div className="flex flex-col items-center gap-2 flex-1">
              {away && <TeamLogo team={away} size="lg" />}
              <span className="text-sm font-semibold text-background-50">{away?.city}{away?.name}</span>
              <span className="text-[11px] text-background-400">{away?.record}</span>
            </div>
          </div>

          <div className="mt-5">
            <VideoPlayerDemo poster={PLAYER_BG} title={`${home?.name} vs ${away?.name}`} isLive={match.status === "live"} />
          </div>

          <section className="mt-6 rounded-lg border border-background-200 bg-background-50 p-5">
            <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
              <span className="flex w-7 h-7 items-center justify-center rounded-md bg-accent-500 text-background-50">
                <i className="ri-file-text-line" />
              </span>
              赛事简介与赛况
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-700">{match.summary}</p>
            <p className="mt-3 text-sm leading-relaxed text-foreground-700">
              本场比赛为{match.stage}阶段的对决，双方均派出主力阵容出战。比赛过程中，两队在高强度对抗下展现了各自鲜明的战术风格，
              场面精彩纷呈，值得篮球迷细细品味。
            </p>
          </section>
        </div>

        <aside className="flex flex-col gap-6">
          <section className="rounded-lg border border-background-200 bg-background-50 p-5">
            <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
              <span className="flex w-7 h-7 items-center justify-center rounded-md bg-primary-500 text-background-50">
                <i className="ri-pulse-line" />
              </span>
              实时比分
            </h2>
            <div className="mt-4 grid grid-cols-2 gap-3">
              <div className="rounded-md bg-background-100 p-3 text-center">
                <p className="text-xs text-foreground-500">{home?.name}</p>
                <p className="mt-1 font-heading text-2xl font-bold text-accent-600">{hasScore ? match.homeScore : "—"}</p>
              </div>
              <div className="rounded-md bg-background-100 p-3 text-center">
                <p className="text-xs text-foreground-500">{away?.name}</p>
                <p className="mt-1 font-heading text-2xl font-bold text-accent-600">{hasScore ? match.awayScore : "—"}</p>
              </div>
            </div>
            <ul className="mt-4 space-y-2 text-sm text-foreground-600">
              <li className="flex justify-between"><span>比赛进度</span><span className="font-medium text-foreground-900">{match.period}</span></li>
              <li className="flex justify-between"><span>赛事阶段</span><span className="font-medium text-foreground-900">{match.stage}</span></li>
              <li className="flex justify-between"><span>在线人数</span><span className="font-medium text-foreground-900">{match.viewers}</span></li>
            </ul>
            <p className="mt-3 text-[11px] text-foreground-400">比分数据为演示用途，非真实赛果。</p>
          </section>

          <section className="rounded-lg border border-background-200 bg-background-50 p-5">
            <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
              <span className="flex w-7 h-7 items-center justify-center rounded-md bg-secondary-500 text-background-50">
                <i className="ri-team-line" />
              </span>
              参赛球队
            </h2>
            <div className="mt-4 flex flex-col gap-4">
              {[home, away].map((team) =>
                team ? (
                  <Link
                    key={team.id}
                    to={`/team/${team.id}`}
                    className="flex items-center gap-3 rounded-md border border-background-200 p-3 transition-colors hover:border-primary-300 hover:bg-background-100"
                  >
                    <TeamLogo team={team} size="md" />
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-foreground-900">{team.city}{team.name}</p>
                      <p className="text-xs text-foreground-500">{team.conference} · {team.record}</p>
                    </div>
                    <i className="ri-arrow-right-s-line ml-auto text-foreground-400" />
                  </Link>
                ) : null,
              )}
            </div>
          </section>
        </aside>
      </section>

      {related.length > 0 && (
        <section className="mt-10">
          <h2 className="mb-5 font-heading text-xl font-bold text-foreground-950 md:text-2xl">相关赛事推荐</h2>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {related.map((m) => {
              const rh = teamMap[m.homeTeamId];
              const ra = teamMap[m.awayTeamId];
              return (
                <Link
                  key={m.id}
                  to={`/match/${m.id}`}
                  className="flex items-center gap-3 rounded-lg border border-background-200 bg-background-50 p-4 transition-colors hover:border-primary-300 hover:bg-background-100"
                >
                  {rh && <TeamLogo team={rh} size="sm" />}
                  <span className="text-sm font-medium text-foreground-800">{rh?.name} vs {ra?.name}</span>
                  <i className="ri-arrow-right-s-line ml-auto text-foreground-400" />
                </Link>
              );
            })}
          </div>
        </section>
      )}
    </div>
  );
}